/* eslint-disable jsx-a11y/no-static-element-interactions */
/* eslint-disable jsx-a11y/click-events-have-key-events */
import React, { useState, useEffect, useRef, useLayoutEffect } from 'react';
import styled from 'styled-components';
import nanoid from 'nanoid';
import PropTypes from 'prop-types';

import { TextField } from '@superops/ui-design-system';
import { SVGIcon } from '@superops/super-icons';

// components
import OutsideClickListener from './components/OutsideClickListener';
import EditAndDeleteOption from './components/EditAndDeleteOption';
import { TEXT_LENGTH } from '@nile/components/src/Constants';

// helpers
import { isAvailable, getFilteredOptions } from './helpers';
import { noop } from 'lodash';

const Wrapper = styled.div`
  position: relative;
  outline: none;
  border: none;
  .popover {
    background-color: #fff;
    border-radius: 5px;
    box-shadow: 0px 10px 30px rgba(0, 0, 0, 0.213032);
    padding: 8px;
    position: absolute;
    bottom: 0;
    left: 0;
    transform: translateY(calc(100% + 5px));
    overflow: auto;
    z-index: 100;
  }
  .textfield {
    border-bottom: 1px solid #ececec;
    .Superops-TextField__Input:active,
    .Superops-TextField__Input:hover,
    .Superops-TextField__Input:focus {
      border-bottom: none !important;
    }
    input {
      ::-webkit-input-placeholder {
        /* WebKit, Blink, Edge */
        color: ${(props) => (props.subtlePlaceholder ? '' : '#000')};
      }
      :-moz-placeholder {
        /* Mozilla Firefox 4 to 18 */
        color: ${(props) => (props.subtlePlaceholder ? '' : '#000')};
        opacity: 1;
      }
      ::-moz-placeholder {
        /* Mozilla Firefox 19+ */
        color: ${(props) => (props.subtlePlaceholder ? '' : '#000')};
        opacity: 1;
      }
      :-ms-input-placeholder {
        /* Internet Explorer 10-11 */
        color: ${(props) => (props.subtlePlaceholder ? '' : '#000')};
      }
      ::-ms-input-placeholder {
        /* Microsoft Edge */
        color: ${(props) => (props.subtlePlaceholder ? '' : '#000')};
      }
      ::placeholder {
        /* Most modern browsers support this now. */
        color: ${(props) => (props.subtlePlaceholder ? '' : '#000')};
      }
    }
  }
  .textfield:has(.Superops-TextField__Input:active, .Superops-TextField__Input:hover, .Superops-TextField__Input:focus) {
    border-bottom: 1px solid #0b0114;
    bacground-color: red;
  }
  .option-list {
    max-height: 200px;
  }
`;

const CreateOptionWrapper = styled.div`
  background-color: #f1f6ff;
  border-radius: 3px;
  color: #000;
  cursor: pointer;
  line-height: 33px;
  min-width: 352px;
  padding: 5px 8px;
`;

const CreateWrapper = styled.div`
  line-height: 33px;
  margin: 5px 0;
  min-width: 352px;
`;

const CreatableDropdown = ({
  options,
  selectedOptions,
  updateOptionsList = (modifiedOptions) => {},
  updateSelectedOptions = (modifiedOptions) => {},
  onOptionEdit,
  onOptionDelete,
  onOptionCreate,
  onOptionEditStateChange = noop,
  isEdit = true,
  isDelete = true,
  isCreate = true,
  isMulti = false,
  disabled = false
}) => {
  const [menuOpen, setMenuOpen] = useState(false);
  const [createOptionState, setCreateOptionState] = useState(false);
  const [newOptionValue, setNewOptionValue] = useState('');
  const [textValue, setTextValue] = useState('');
  const [currentFocus, setCurrentFocus] = useState(0);

  const wrapperRef = useRef();

  const createOptionRef = useRef();

  const textFieldDisabled =
    disabled || options.reduce((acc, { editState }) => acc || editState, false);

  const closeMenu = () => {
    setMenuOpen(false);
  };

  const selectOptions = (option, closeMenuAfterSelect = true) => {
    if (isMulti) {
      updateSelectedOptions([...selectedOptions, option]);
      // multiselect logic to be coded
    } else {
      updateSelectedOptions([option]);
      setTextValue('');
    }
    if (closeMenuAfterSelect) {
      closeMenu();
      // remove focus from input
      document.activeElement.blur();
    }
  };

  const handleTextChange = (value) => {
    setTextValue(value);
  };

  const handleTextBlur = () => {
    setTextValue(textValue.trim());
  };

  const handleNewOptionTextChange = (value) => {
    setNewOptionValue(value);
  };

  const handleNewOptionBlur = () => {
    setNewOptionValue('');
    setCreateOptionState(false);
  };

  const createOption = (value) => {
    const newOption = { label: value, value, id: `${nanoid()}`, isNew: true };
    if (onOptionCreate) onOptionCreate(newOption);
    updateOptionsList([...options, newOption]);
    selectOptions(newOption);
  };

  const handleAcceptNewOption = (event) => {
    if (event.key === 'Enter') {
      event.stopPropagation();
      const value = newOptionValue.trim();
      if (value) {
        handleNewOptionBlur();
        const availableOption = isAvailable(options, value);
        if (availableOption) {
          selectOptions(availableOption);
          return;
        }
        createOption(value);
      }
    }
  };

  const updateLocalOptions = (option, value, mode) => {
    const localOptionsCopy = [...options];

    if (mode === 'editState') {
      const editedOptionIndex = options.findIndex((el) => {
        if (el.id) return el.id === option.id;
        return el.label === option.label;
      });
      localOptionsCopy[editedOptionIndex].editState = value;
      onOptionEditStateChange(option);
      updateOptionsList(localOptionsCopy);
    }

    if (mode === 'edit') {
      if (isAvailable(options, value)) return;
      const editedOptionIndex = options.findIndex((el) => {
        if (el.id) return el.id === option.id;
        return el.label === option.label;
      });
      localOptionsCopy[editedOptionIndex].value = value;
      localOptionsCopy[editedOptionIndex].label = value;
      if (onOptionEdit) onOptionEdit(localOptionsCopy[editedOptionIndex]);
      updateOptionsList(localOptionsCopy);

      const editedOptionSelectedIdx = selectedOptions.findIndex(
        ({ id }) => id === option.id
      );
      if (editedOptionSelectedIdx > -1) {
        const selectedOptionsCopy = [...selectedOptions];
        selectedOptionsCopy[editedOptionSelectedIdx].value = value;
        selectedOptionsCopy[editedOptionSelectedIdx].label = value;
        updateSelectedOptions(selectedOptionsCopy);
      }
    }

    if (mode === 'delete') {
      const updatedLocalOptions = options.filter((el) => {
        if (el.id) return el.id !== option.id;
        return el.label !== option.label;
      });
      if (onOptionDelete)
        onOptionDelete(options.find((el) => el.id === option.id));
      updateOptionsList(updatedLocalOptions);

      const deletedOptionSelectedIdx = selectedOptions.findIndex(
        ({ id }) => id === option.id
      );
      if (deletedOptionSelectedIdx > -1) {
        const updatedSelectedOptions = selectedOptions.filter((el) => {
          if (el.id) return el.id !== option.id;
          return el.label !== option.label;
        });
        updateSelectedOptions(updatedSelectedOptions);
      }
    }
  };

  const getTextValuePlaceholder = () => {
    if (isMulti) {
      // multiselect logic to be coded
      return '';
    }
    return selectedOptions[0]?.label || 'Enter category';
  };

  useEffect(() => {
    if (currentFocus > getFilteredOptions(options, textValue)?.length - 1) {
      setCurrentFocus(0);
    }
  }, [currentFocus, options, textValue]);

  const focusElementAfterEdit = () => {
    if (wrapperRef.current) wrapperRef.current.focus();
  };

  const handleKeyDown = (e) => {
    if (createOptionState) return; // ignore keydown events if create state is enabled
    const size = getFilteredOptions(options, textValue)?.length;
    if (e.keyCode === 40) {
      // Down arrow
      e.preventDefault();
      setCurrentFocus(currentFocus >= size - 1 ? 0 : currentFocus + 1);
    } else if (e.keyCode === 38) {
      // Up arrow
      e.preventDefault();
      setCurrentFocus(currentFocus === 0 ? size - 1 : currentFocus - 1);
    }

    if (e.key === 'Enter') {
      // Enter
      const focusedOption = getFilteredOptions(options, textValue)[
        currentFocus
      ];
      if (focusedOption) {
        selectOptions(focusedOption);
      }
      // Handling for automated create option
      if (!size && isCreate) {
        createOption(textValue.trim());
        setTextValue('');
      }
    }
  };

  useLayoutEffect(() => {
    // To scroll and show all the available options in a list
    if (menuOpen) {
      setTimeout(() => {
        if (createOptionRef.current) {
          createOptionRef.current.scrollIntoView({
            behavior: 'smooth',
            block: 'end',
            inline: 'nearest'
          });
        }
      }, 100);
    }
  }, [menuOpen]);

  return (
    <Wrapper
      className="fw flex fcol"
      ref={wrapperRef}
      onKeyDown={handleKeyDown}
      tabIndex={-1}
      subtlePlaceholder={!selectedOptions[0]?.label}
    >
      <div
        className="textfield flex center fw cursor-pointer"
        onClick={() => {
          if (textFieldDisabled) return;
          if (!menuOpen) setMenuOpen(true);
        }}
      >
        <div className="f11">
          <TextField
            value={textValue}
            onChange={handleTextChange}
            onBlur={handleTextBlur}
            disabled={textFieldDisabled}
            placeholder={getTextValuePlaceholder()}
            autoComplete="off"
            noborder
            maxLength={TEXT_LENGTH.size_5}
          />
        </div>
        <SVGIcon name="ArrowDown" />
      </div>
      {menuOpen && (
        <div className="popover">
          <div className="flex fcol fhp">
            {!createOptionState && (
              <div className="flex fcol f11 scroll option-list">
                {getFilteredOptions(options, textValue).map((option, index) => (
                  <EditAndDeleteOption
                    key={option.id || option.label}
                    option={option}
                    updateOptions={updateLocalOptions}
                    selectOption={selectOptions}
                    isSelected={selectedOptions
                      .map(({ label }) => label)
                      .includes(option.label)}
                    isEdit={isEdit}
                    isDelete={isDelete}
                    focus={currentFocus === index}
                    setFocus={setCurrentFocus}
                    index={index}
                    focusElementAfterEdit={focusElementAfterEdit}
                  />
                ))}
              </div>
            )}
            {isCreate &&
              options?.length > 0 &&
              !getFilteredOptions(options, textValue).length && (
                <CreateOptionWrapper
                  onClick={() => {
                    createOption(textValue.trim());
                  }}
                >
                  {`Create "${textValue}"`}
                </CreateOptionWrapper>
              )}
            {isCreate &&
              (!!getFilteredOptions(options, textValue).length ||
                !options?.length) && (
                <CreateWrapper className="flex p5 center">
                  <SVGIcon name="RuleAdd" color="#8CC450" size="sm" />
                  <span
                    className="ml8 mr8 link-block"
                    ref={createOptionRef}
                    onClick={() => {
                      setCreateOptionState(true);
                      if (!newOptionValue) setNewOptionValue(textValue.trim());
                    }}
                  >
                    Create Option
                  </span>

                  {createOptionState && (
                    <div onKeyDown={handleAcceptNewOption}>
                      <TextField
                        value={newOptionValue}
                        onChange={handleNewOptionTextChange}
                        onBlur={handleNewOptionBlur}
                        placeholder="Enter value"
                        autoComplete="off"
                        focused
                        maxLength={TEXT_LENGTH.size_5}
                      />
                    </div>
                  )}
                </CreateWrapper>
              )}
          </div>
        </div>
      )}
      <OutsideClickListener
        reference={wrapperRef}
        onOutsideClick={() => {
          setCreateOptionState(false);
          setTextValue('');
          closeMenu();
        }}
      />
    </Wrapper>
  );
};

const OptionShape = {
  value: PropTypes.string,
  label: PropTypes.string,
  editState: PropTypes.bool,
  id: PropTypes.string
};

CreatableDropdown.defaultProps = {
  onOptionEdit: () => {},
  onOptionDelete: () => {},
  onOptionCreate: () => {},
  isEdit: true,
  isDelete: true,
  isCreate: true,
  isMulti: false,
  disabled: false
};

CreatableDropdown.propTypes = {
  options: PropTypes.arrayOf(PropTypes.shape(OptionShape)).isRequired,
  selectedOptions: PropTypes.arrayOf(PropTypes.shape(OptionShape)).isRequired,
  updateOptionsList: PropTypes.func.isRequired,
  updateSelectedOptions: PropTypes.func.isRequired,
  onOptionEdit: PropTypes.func,
  onOptionDelete: PropTypes.func,
  onOptionCreate: PropTypes.func,
  isEdit: PropTypes.bool,
  isDelete: PropTypes.bool,
  isCreate: PropTypes.bool,
  isMulti: PropTypes.bool,
  disabled: PropTypes.bool
};

export default CreatableDropdown;

export { Wrapper, CreateOptionWrapper, CreateWrapper };
